import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ClipboardText,
  CheckCircle,
  XCircle,
  Clock,
  MapPin,
  Calendar,
  Warning,
  User,
  Lightning,
} from '@phosphor-icons/react';

export const MyAssignmentsPanel = () => {
  const { api, user } = useAuth();
  
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optOutReasons, setOptOutReasons] = useState([]);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [declineNote, setDeclineNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAssignments = useCallback(async () => {
    try {
      const response = await api.get('/my/assignments');
      setAssignments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchOptOutReasons = useCallback(async () => {
    try {
      const response = await api.get('/opt-out-reasons');
      setOptOutReasons(response.data || []);
    } catch (error) {
      console.error('Failed to fetch opt-out reasons:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchAssignments();
    fetchOptOutReasons();
    
    // Refresh every 30 seconds for timer updates
    const interval = setInterval(fetchAssignments, 30000);
    return () => clearInterval(interval);
  }, [fetchAssignments, fetchOptOutReasons]);

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expired';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAcknowledge = async (ackId) => {
    try {
      await api.post(`/assignments/${ackId}/acknowledge`);
      toast.success('Assignment acknowledged');
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  const handleDeclineClick = (assignment) => {
    setSelectedAssignment(assignment);
    setSelectedReason('');
    setDeclineNote('');
    setDeclineDialogOpen(true);
  };

  const handleDecline = async () => {
    if (!selectedAssignment?.acknowledgement?.id || !selectedReason) return;
    
    const reason = optOutReasons.find(r => r.id === selectedReason);
    if (reason?.requires_note && !declineNote) {
      toast.error('Note is required for this reason');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/assignments/${selectedAssignment.acknowledgement.id}/decline`, {
        opt_out_reason_id: selectedReason,
        opt_out_note: declineNote || undefined
      });
      toast.success('Assignment declined');
      setDeclineDialogOpen(false);
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    } finally {
      setSubmitting(false);
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'forced_fill':
        return <Badge className="bg-orange-100 text-orange-800"><Lightning size={12} className="mr-1" />Forced</Badge>;
      case 'supervisor_override':
        return <Badge className="bg-purple-100 text-purple-800"><User size={12} className="mr-1" />Override</Badge>;
      default:
        return <Badge variant="outline"><ClipboardText size={12} className="mr-1" />Voluntary</Badge>;
    }
  };

  const getStatusBadge = (ack) => {
    if (!ack) return null;
    
    switch (ack.status) {
      case 'acknowledged':
        return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle size={12} className="mr-1" />Acknowledged</Badge>;
      case 'declined':
        return <Badge className="bg-red-100 text-red-800"><XCircle size={12} className="mr-1" />Declined</Badge>;
      case 'expired':
        return <Badge className="bg-slate-100 text-slate-800"><Clock size={12} className="mr-1" />Expired</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock size={12} className="mr-1" />Pending</Badge>;
    }
  };

  const pendingAssignments = assignments.filter(a => a.acknowledgement?.status === 'pending');
  const otherAssignments = assignments.filter(a => a.acknowledgement?.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="my-assignments-panel">
      {/* Pending Acknowledgements */}
      {pendingAssignments.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Clock size={20} weight="fill" />
              Pending Acknowledgements ({pendingAssignments.length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              Please acknowledge or decline these assignments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingAssignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="p-4 bg-white border border-amber-200 rounded-lg"
                  data-testid={`pending-assignment-${assignment.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900">
                          {assignment.job_title}
                        </h4>
                        {getSourceBadge(assignment.assigned_via)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-2">
                        <Badge variant="secondary">{assignment.crew_role_name}</Badge>
                        {assignment.job_location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {assignment.job_location}
                          </span>
                        )}
                        {assignment.job_start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {assignment.job_start_date}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-amber-700">
                        <Clock size={16} />
                        <span className="font-mono font-medium">
                          {formatTimeRemaining(assignment.time_remaining)}
                        </span>
                        <span className="text-sm">remaining</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => handleAcknowledge(assignment.acknowledgement?.id)}
                        data-testid={`ack-btn-${assignment.id}`}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Acknowledge
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => handleDeclineClick(assignment)}
                        data-testid={`decline-btn-${assignment.id}`}
                      >
                        <XCircle size={14} className="mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardText size={20} />
            My Assignments
          </CardTitle>
          <CardDescription>
            Your current crew assignments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ClipboardText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No assignments yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {otherAssignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="p-4 border border-slate-200 rounded-lg"
                  data-testid={`assignment-${assignment.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900">
                          {assignment.job_title}
                        </h4>
                        {getSourceBadge(assignment.assigned_via)}
                        {getStatusBadge(assignment.acknowledgement)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                        <Badge variant="secondary">{assignment.crew_role_name}</Badge>
                        {assignment.job_location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {assignment.job_location}
                          </span>
                        )}
                        {assignment.job_start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {assignment.job_start_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Assignment</DialogTitle>
            <DialogDescription>
              Please select a reason for declining this assignment
            </DialogDescription>
          </DialogHeader>

          {selectedAssignment && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium">{selectedAssignment.job_title}</h4>
                <p className="text-sm text-slate-500">Role: {selectedAssignment.crew_role_name}</p>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Warning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Declining this assignment will be recorded and may be reviewed. 
                    The slot may be reassigned to another employee.
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="decline-reason">Reason *</Label>
                <Select value={selectedReason} onValueChange={setSelectedReason}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {optOutReasons.map((reason) => (
                      <SelectItem key={reason.id} value={reason.id}>
                        {reason.label}
                        {reason.requires_note && <span className="text-slate-400 ml-1">(note required)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {optOutReasons.find(r => r.id === selectedReason)?.requires_note && (
                <div>
                  <Label htmlFor="decline-note">Note *</Label>
                  <Textarea
                    id="decline-note"
                    value={declineNote}
                    onChange={(e) => setDeclineNote(e.target.value)}
                    placeholder="Please provide additional details..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDecline}
              disabled={submitting || !selectedReason}
              variant="destructive"
              data-testid="confirm-decline-btn"
            >
              {submitting ? 'Declining...' : 'Confirm Decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyAssignmentsPanel;
